import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ArrowClockwise, Trash, Fire, ChatCircleDots, Pulse, Sparkle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(false);
    api.get("/admin/analytics").then(({ data }) => setData(data)).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = async (mode) => {
    setBusy(true);
    try {
      await api.post("/admin/analytics/reset", { mode });
      toast.success(mode === "now" ? "Stats reset — counting from now" : "Showing all-time stats again");
      setResetOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-14" data-testid="admin-analytics">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="label-eyebrow">Product analytics</span>
          <h2 className="font-display font-light tracking-tighter text-2xl md:text-3xl mt-1">How members actually use Ikavio</h2>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} data-testid="analytics-refresh-btn" className="inline-flex items-center gap-1.5 text-xs text-brand hover:underline font-medium">
            <ArrowClockwise size={13} weight="bold" /> Refresh
          </button>
          <button onClick={() => setResetOpen(true)} data-testid="analytics-reset-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium border border-border rounded-full px-3.5 py-1.5 hover:bg-secondary transition-colors">
            <Trash size={13} weight="bold" /> Reset stats
          </button>
        </div>
      </div>

      {error && (
        <div className="clay p-8 mt-6 text-center text-sm text-muted2" data-testid="analytics-error">
          Couldn't load analytics. <button onClick={load} className="text-brand hover:underline font-medium">Retry</button>
        </div>
      )}
      {!data && !error && <div className="clay p-8 mt-6 text-sm text-muted2">Crunching member data…</div>}

      {data && (
        <>
          <p className="text-xs text-muted2 mt-3" data-testid="analytics-baseline">
            {data.since
              ? `Counting from ${new Date(data.since).toLocaleString()} (stats were reset)`
              : "All-time, since your first member"}
            {data.excluded_admins > 0 && ` · ${data.excluded_admins} founder account${data.excluded_admins > 1 ? "s" : ""} excluded`}
          </p>

          <div className="clay p-6 sm:p-8 mt-4" data-testid="analytics-funnel">
            <span className="label-eyebrow">Activation funnel</span>
            <div className="mt-5 space-y-3.5">
              {data.funnel.map((s) => (
                <div key={s.label} data-testid={`funnel-${s.label.replace(/\W+/g, "-").toLowerCase()}`}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-ink/85">{s.label}</span>
                    <span className="font-medium">{s.value}<span className="text-muted2 font-normal text-xs ml-1.5">{s.pct}%</span></span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary mt-1.5 overflow-hidden">
                    <div className="h-full rounded-full bg-brand transition-[width] duration-700" style={{ width: `${Math.max(s.pct, 1)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <Stat label="Active today" value={data.engagement.active_today} sub={`${data.engagement.active_7d} in last 7 days`} icon={Pulse} color="var(--brand)" testId="analytics-active" />
            <Stat label="D7 retention" value={`${data.engagement.d7_retention}%`} sub={`of ${data.engagement.d7_cohort} eligible members`} icon={Sparkle} color="var(--sage)" testId="analytics-retention" />
            <Stat label="Avg streak" value={data.engagement.avg_streak} sub={`best ever ${data.engagement.best_streak} days`} icon={Fire} color="#E8A54B" testId="analytics-streak" />
            <Stat label="Coach users" value={data.engagement.coach_users} sub={`${data.engagement.weekly_reviews} weekly reviews`} icon={ChatCircleDots} color="#8B7CF6" testId="analytics-coach" />
          </div>

          <div className="clay p-6 sm:p-8 mt-6" data-testid="analytics-trend">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">Last 14 days</span>
              <span className="text-xs text-muted2">
                <b className="text-brand">{data.trend.signups.reduce((a, b) => a + b, 0)}</b> signups ·{" "}
                <b className="text-ink">{data.trend.checkins.reduce((a, b) => a + b, 0)}</b> check-ins
              </span>
            </div>
            <Bars days={data.trend.days} signups={data.trend.signups} checkins={data.trend.checkins} />
            <div className="flex items-center gap-4 mt-3 text-[11px] text-muted2">
              <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-brand inline-block" /> Signups</span>
              <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-ink opacity-25 inline-block" /> Check-ins</span>
              <span className="ml-auto">{new Date(data.trend.days[0]).toLocaleDateString(undefined, { month: "short", day: "numeric" })} → today</span>
            </div>
            <p className="text-xs text-muted2 mt-4">
              {data.engagement.checkins_total} check-ins total · {data.engagement.avg_checkins_per_member} per member · {data.engagement.active_trials} active trials
            </p>
          </div>
        </>
      )}

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="analytics-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-light text-xl">Reset stats</DialogTitle>
            <DialogDescription>
              This only moves the counting baseline — no member accounts, check-ins or revenue data are ever deleted. Useful to zero out pre-launch test traffic.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button data-testid="analytics-reset-now-btn" onClick={() => reset("now")} disabled={busy}
              className="w-full bg-brand text-white rounded-2xl py-3.5 text-sm font-medium hover:bg-ink transition-colors disabled:opacity-50">
              {busy ? "Working…" : "Start counting from now"}
            </button>
            <button data-testid="analytics-reset-alltime-btn" onClick={() => reset("all_time")} disabled={busy}
              className="w-full border border-border rounded-2xl py-3.5 text-sm font-medium hover:bg-secondary transition-colors disabled:opacity-50">
              Show all-time stats again
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Stat({ label, value, sub, icon: Icon, color, testId }) {
  return (
    <div className="clay p-5" data-testid={testId}>
      <Icon size={18} weight="fill" color={color} />
      <div className="font-display text-3xl font-light tracking-tight mt-2">{value}</div>
      <div className="text-xs text-muted2 mt-0.5">{label}</div>
      <div className="text-[11px] text-muted2/80 mt-0.5">{sub}</div>
    </div>
  );
}

function Bars({ days, signups, checkins }) {
  const max = Math.max(1, ...signups, ...checkins);
  return (
    <div className="flex items-end gap-1.5 h-28 mt-5">
      {days.map((d, i) => (
        <div key={d} className="flex-1 flex items-end gap-[2px] h-full group relative" title={`${d}: ${signups[i]} signups, ${checkins[i]} check-ins`}>
          <div className="flex-1 bg-brand rounded-t-md min-h-[2px] transition-all" style={{ height: `${(signups[i] / max) * 100}%` }} />
          <div className="flex-1 bg-ink opacity-25 rounded-t-md min-h-[2px] transition-all" style={{ height: `${(checkins[i] / max) * 100}%` }} />
        </div>
      ))}
    </div>
  );
}
